import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Translation } from 'react-i18next';
import i18n from 'i18next';
import getTemplate from '../../utils/gettemplate';
import CustomTemplate from '../customtemplate/customtemplate.jsx';
import SearchForm from '../searchform/searchform.jsx';
import Filters from '../filters/filters.jsx';
import FiltersModal from '../filtersModal/filtersModal.jsx';
import Locations from '../locations/locations.jsx';
import LocationsList from '../locationsList/locationsList.jsx';
import UseCurrentLocation from '../usecurrentlocation/usecurrentlocation.jsx';
import './ctablock.scss';

const CtaBlock = (props) => {
  // Filter options
  const inlineFiltersOptions = ['inline', 'inline-buttons-below'];
  const panelFiltersOptions = ['panel'];

  // Component States
  const [resetInputIdLocation, setResetInputIdLocation] = useState(false);
  const [showFilters, setShowFilters] = useState(props.showFilters || false);

  // Utility function used by this file and child component
  const canSearch = () => {
    // determine if we can search
    // no errors getting the user location or if they have manually entered a search
    return props.locationStatus !== 'error' || (props.locations.length !== 0 && props.loadingStatus);
  };

  // Utility function used by child component
  const anyFiltersActive = () => {
    // are there any filters activated by the user
    return props.servicesFilters.length || props.distanceFilter.length;
  };

  const handleClickHideModal = () => {
    setShowFilters(false);
    document.getElementById('id_location').focus();
  };

  const handleSearchSubmit = (event) => {
    const idLocation = document.getElementById('id_location');
    event.preventDefault();
    props.setLocationStatus('manual');
    props.setUserLocation({
      userLocationString: idLocation.getAttribute('value'), 
      userLatitude: null, 
      userLongitude: null,
    });
  };

  const handleViewList = () => {
    props.setSelectedLocationId(null);
  };

  // Create html child elements
  const createFilters = () => {
    // create a list of filter JSX elements
    const filtersList = [];
    props.filters.forEach((filter) => {
      filtersList.push(
        <Filters
          distanceFilter={props.distanceFilter}
          servicesFilters={props.servicesFilters}
          setServicesFilters={props.setServicesFilters}
          filter={filter}
          key={filter.label}
          label={filter.label}
        />,
      );
    });
    return filtersList;
  };

  useEffect(() => {
    if (resetInputIdLocation === true) {
      setResetInputIdLocation(false);
    }
  });

  useEffect(() => {
    // toggles focuses on/off element that contains filters when modal opened/closed
    const modalFilters = document.getElementById('modalFilters');
    if (modalFilters) {
      modalFilters.focus();
    }
  }, [showFilters]);

  let e;
  if (window.CustomEvent && typeof window.CustomEvent === 'function') {
    e = new CustomEvent('sbevents:locationfinder:loaded', { bubbles: true, cancelable: false });
  } else {
    e = document.createEvent('CustomEvent');
    e.initCustomEvent('sbevents:locationfinder:loaded', true, true);
  }
  document.dispatchEvent(e);

  // Element variables
  const ButtonTemplate = getTemplate(props, 'search-button');
  let CustomButton;
  let CustomFiltersLabel;
  const FiltersListContent = createFilters();
  let LocationsListBar;
  const TemplateMessage = getTemplate(props, 'filters-message');

  const LocationsJSX = (
    <Locations
      activeFilters={anyFiltersActive()}
      customTemplates={props.customTemplates}
      disableGeolocation={props.disableGeolocation}
      error={props.error}
      setCenterPosition={props.setCenterPosition}
      loadingStatus={props.loadingStatus}
      locations={props.locations}
      searchable={canSearch()}
      selectedLocationId={props.selectedLocationId}
      setSelectedLocationId={props.setSelectedLocationId}
      toggleDisplayInfoWindow={props.toggleDisplayInfoWindow}
    />
  );
  
  // SearchForm and child components
  // Filters have two display options pulled from the config (filterLayout) panel or inline
  if (ButtonTemplate) {
    CustomButton = <CustomTemplate ElementType="span" template={ButtonTemplate} />;
  }

  if (TemplateMessage) {
    CustomFiltersLabel = <CustomTemplate ElementType="span" template={TemplateMessage} />;
  }

  const FiltersInline = <div className="sb-filters-inline">
    {FiltersListContent}{CustomFiltersLabel}
  </div>;

  const FilterActivator = (
    <button
      className="sb-filter-results"
      id="open-filter-modal"
      onClick={() => { setShowFilters(true); }}
      aria-label={i18n.t('filterResultsDescription')}
      aria-controls="Google-Map-Filter-Modal"
    >
      <Translation>{(t) => t('Advanced search')}</Translation>
    </button>
  );

  const CtaSidebar = (
    <>
      <div className="sb-search-form">
        <SearchForm
          customTemplates={props.customTemplates}
          filtersLayout={props.filtersLayout}
          handleSearchSubmit={handleSearchSubmit}
          resetInputIdLocation={resetInputIdLocation}
          searchInputPlaceholderText={props.searchInputPlaceholderText}
          searchInputType={props.searchInputType}
          setUserLocation={props.setUserLocation}
          userLocation={props.userLocation}
        />
        {inlineFiltersOptions.includes(props.filtersLayout) && canSearch() ? FiltersInline : ''}
        {props.filtersLayout === 'inline-buttons-below' ? <form onSubmit={handleSearchSubmit}>{CustomButton || <Translation>{(t) => <button aria-label={t('searchWithFilters')} type="submit">{t('Go')}</button> }</Translation>}</form> : ''}
        {
          (props.disableGeolocation === false && props.locationStatus !== 'error')
          && <UseCurrentLocation
              customTemplates={props.customTemplates}
              setUserLocation={props.setUserLocation}
              setLocationStatus={props.setLocationStatus}
            />
        }
        {panelFiltersOptions.includes(props.filtersLayout) && canSearch() ? FilterActivator : ''}
      </div>
      <div
        className={`sb-locations ${props.selectedLocationId ? 'sb-has-selected-location' : ''}`}
        aria-label={i18n.t('locationsList')}
        id="locations-list"
        role="list"
      >
        {LocationsJSX}
      </div>
    </>
  );

  // Other Ctablock child components
  const FiltersModalComponent = (
    <FiltersModal
      handleClickHideFilters={handleClickHideModal}
      filtersListContent={FiltersListContent}
      loadingStatus={props.loadingStatus}
      locations={props.locations}
      matchesFilterCount={props.matchesFilterCount}
    />
  );

  return (
    <>
      {CtaSidebar}
      {showFilters ? FiltersModalComponent : ''}
      {LocationsListBar}
    </>
  );
};

CtaBlock.propTypes = {
  customTemplates: PropTypes.object,
  disableGeolocation: PropTypes.bool,
  distanceFilter: PropTypes.string,
  error: PropTypes.bool,
  filters: PropTypes.array,
  filtersLayout: PropTypes.string,
  loadingStatus: PropTypes.bool,
  locations: PropTypes.array,
  locationStatus: PropTypes.string,
  matchesFilterCount: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  searchInputPlaceholderText: PropTypes.string,
  searchInputType: PropTypes.string,
  selectedLocationId: PropTypes.string,
  setCenterPosition: PropTypes.func,
  servicesFilters: PropTypes.array,
  setLocationStatus: PropTypes.func,
  setSelectedLocationId: PropTypes.func,
  setServicesFilters: PropTypes.func,
  setUserLocation: PropTypes.func,
  showFilters: PropTypes.bool,
  toggleDisplayInfoWindow: PropTypes.func,
  userLocation: PropTypes.object,
};

export default CtaBlock;
